import {
  observable,
  computed,
  action,
  autorun,
  toJS,
  reaction,
  makeObservable,
  runInAction,
} from 'mobx';

import Cookies from 'js-cookie';
import { log } from 'common/utils/logging.util';

import { AuthError, captureError } from 'common/utils/error.util';
import { AccountService } from 'common/services/account.service';
import {
  setAmpSessionId,
  setAmpUserId,
} from 'common/utils/client-analytics.util';
import { getFingerprint } from 'common/bridges/fingerprint.bridge';
import { setAuthCookie } from 'common/bridges/cookies.bridge';

const ORGANIC_AFF_CXD = '2287_356059';

class Auth {
  @observable session = {};

  @observable isLoaded = false;

  @observable initialQueryParams = {};

  constructor() {
    makeObservable(this);
    autorun(() => {
      log(`session: ${JSON.stringify(toJS(this.session))}`);
      log(`isLoaded: ${this.isLoaded}`);
    });
    reaction(
      () => this.authToken,
      (token) => {
        setAmpSessionId(token);
      }
    );
    reaction(
      () => this.userGUID,
      (guid) => {
        setAmpUserId(guid);
      }
    );
  }

  @computed get isAuthorised() {
    return this.session.is_authorised;
  }

  @computed get authToken() {
    return this.session.app_token || Cookies.get('exprowhite_trading');
  }

  @computed get loginUrl() {
    return this.session.login_url;
  }

  @computed get registrationUrl() {
    return this.session.registration_url;
  }

  @computed get getAuthToken() {
    return this.session.auth_token;
  }

  @computed get userGUID() {
    return this.session.user_guid;
  }

  @computed get appGuid() {
    return this.session.app_guid;
  }

  @action setInitialQueryParams = (params) => {
    this.initialQueryParams = { ...this.initialQueryParams, ...params };
  };

  @action createAndSetSession = async ({ restoreId }) => {
    try {
      const {
        agent_id = '',
        subid = '',
        _ga = '',
        _gid = '',
        cxd = ORGANIC_AFF_CXD,
        ...urlParams
      } = this.initialQueryParams;
      const { hash, components } = await getFingerprint();
      const ga = _ga || Cookies.get('_ga');
      const gid = _gid || Cookies.get('_gid');

      log(
        `queryParams: ${JSON.stringify(toJS(this.initialQueryParams))}`,
        'warn'
      );

      const data = {
        app_session: {
          host: window.location.host,
        },
        agentid: agent_id,
        claims: {
          cid: hash,
          ga,
          gid,
          fingerprint: components.reduce((acc, val) => {
            if (val.key === 'canvas' || val.key === 'webgl') {
              return acc;
            }
            return {
              ...acc,
              [val.key]: val.value.toString(),
            };
          }, {}),
          params: { subid, cxd, ...urlParams },
        },
      };

      if (restoreId) {
        data.claims.restoreId = restoreId;
      }

      const session = await AccountService.createSession(data);
      runInAction(() => {
        this.session = session;
      });
    } catch (error) {
      captureError(new AuthError(`cant create session: ${error}`));
    }
  };

  @action setSessionMetadata = async ({ restoreId }) => {
    try {
      const session = await AccountService.getSession(this.authToken);
      runInAction(() => {
        this.session = session;
      });
    } catch (error) {
      await this.createAndSetSession({ restoreId });
      this.setSessionMetadata({ restoreId });
    }
  };

  @action async initAuth({ isForced = false, restoreId } = {}) {
    if (!isForced) {
      runInAction(() => {
        this.isLoaded = false;
      });
    }

    const query = new URLSearchParams(window.location.search);
    const token = query.get('session') || this.initialQueryParams?.session;
    if (token) {
      runInAction(() => {
        this.session.app_token = token;
      });
    }

    if (!this.authToken || isForced)
      await this.createAndSetSession({ restoreId });
    await this.setSessionMetadata({ restoreId });

    window.parent.postMessage('rw_session_ready', '*');
    runInAction(() => {
      this.isLoaded = true;
    });
    if (this.authToken) {
      setAuthCookie(this.authToken);
    }

    return this.session;
  }
}

const AuthStore = new Auth();

export default AuthStore;
